import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Bulk-remove every cart line belonging to a single Case (group of lines
   * sharing the same `_case_id` line property). The cart drawer collapses
   * each case into one row, so the row's remove button must zero ALL its
   * underlying components in one `/cart/update.js` call — otherwise we'd
   * leave 3 of 4 wines behind and the row would just re-render with the
   * remainder.
   *
   * @param {Event} event - Click event from the case row's remove button.
   */
  onCaseRemove(event) {
    // The visible row carries a comma-separated list of every line key in
    // the case (server-side aggregated in cart-products.liquid). That's the
    // authoritative source — the collapsed row only references its own
    // line.key, but we need to zero ALL components in one call.
    const row =
      /** @type {HTMLElement|null} */ (event.target?.closest?.('[data-case-keys]'));
    if (!row) return;
    const caseId = row.dataset.caseId;
    const keysRaw = row.dataset.caseKeys || '';
    const keys = keysRaw.split(',').filter(Boolean);
    if (!keys.length) return;

    /** @type {Record<string, 0>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    this.#disableCartItems();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      sections_url: window.location.pathname,
    });

    // Animate the collapsed row out so the removal feels consistent with
    // single-line removals. The cart re-render below replaces the markup.
    if (!prefersReducedMotion()) {
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    }

    // Optimistic empty-state: if every other rendered row is part of this
    // case, swap in the empty-cart template so the customer sees an instant
    // empty drawer instead of a flash of half-loaded markup.
    const otherRows = this.refs.cartItemRows.filter((r) => r !== row);
    const isEmptyCart = otherRows.length === 0;
    if (isEmptyCart) {
      const template = document.getElementById('empty-cart-template');
      if (template instanceof HTMLTemplateElement) {
        const clone = document.importNode(template.content, true);
        startViewTransition(
          () => this.replaceChildren(clone),
          [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page'],
        );
      }
    }

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        const newCartHiddenItemCount = new DOMParser()
          .parseFromString(parsedResponseText.sections?.[this.sectionId] || '', 'text/html')
          .querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (parsedResponseText.sections?.[this.sectionId]) {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Bundle case row +/- stepper. Scales every component line in the case
   * by its `_per_case_qty` ratio (stamped at add time by
   * `assets/product-buy-flow.js`). Case-builder cases don't use this — they
   * surface a "see contents" edit link that returns the customer to the PDP.
   *
   * `data-case-key-units` on the row is a comma-separated `key:per_unit` map
   * for every component line in the case. `data-case-qty` is the current
   * bundle multiplier. On +/-, each line's target quantity is
   * `per_unit × new_case_qty`. At new_case_qty == 0 every component goes
   * to 0 — the cart re-render will drop the row entirely.
   *
   * @param {Event} event
   */
  onCaseQty(event) {
    const btn = /** @type {HTMLElement|null} */ (event.target?.closest?.('[data-action]'));
    const row = /** @type {HTMLElement|null} */ (event.target?.closest?.('[data-case-keys]'));
    if (!btn || !row) return;
    const dir = btn.dataset.action === 'inc' ? 1 : -1;
    const current = parseInt(row.dataset.caseQty || '1', 10) || 1;
    const next = Math.max(0, current + dir);
    if (next === current) return;
    const unitsRaw = row.dataset.caseKeyUnits || '';
    /** @type {Record<string, number>} */
    const updates = {};
    // Each segment is `<line.key>=<per_unit>`. Line keys themselves contain
    // colons (Shopify format `<variant_id>:<hex>`), so `=` is the pair
    // separator — splitting on `:` would chop the key in half and treat the
    // hex suffix as the multiplier (× 100+).
    for (const entry of unitsRaw.split(',').filter(Boolean)) {
      const idx = entry.lastIndexOf('=');
      if (idx === -1) continue;
      const key = entry.slice(0, idx);
      const per = parseInt(entry.slice(idx + 1) || '1', 10) || 1;
      if (!key) continue;
      updates[key] = per * next;
    }
    if (!Object.keys(updates).length) return;

    // Hitting 0 = remove the case. Animate the row out so the cart re-render
    // doesn't feel like a flash.
    if (next === 0 && !prefersReducedMotion()) {
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    }

    this.#disableCartItems();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((c) => {
      if (c instanceof HTMLElement && c.dataset.sectionId) sectionsToUpdate.add(c.dataset.sectionId);
    });
    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      sections_url: window.location.pathname,
    });
    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((res) => res.text())
      .then((text) => {
        const parsed = JSON.parse(text);
        resetShimmer(this);
        const newHiddenCount = new DOMParser()
          .parseFromString(parsed.sections?.[this.sectionId] || '', 'text/html')
          .querySelector('[ref="cartItemCount"]')?.textContent;
        const itemCount = newHiddenCount ? parseInt(newHiddenCount, 10) : 0;
        this.dispatchEvent(
          new CartUpdateEvent(parsed, this.sectionId, {
            itemCount,
            source: 'cart-items-component',
            sections: parsed.sections,
          })
        );
        if (parsed.sections?.[this.sectionId]) {
          morphSection(this.sectionId, parsed.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((err) => console.error(err))
      .finally(() => this.#enableCartItems());
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
